/** @jsxImportSource @emotion/react */
import React, { forwardRef, memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectFromIpEqpParaMaster } from 'apis/ip/IpEqpPara';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import CustomGrid from 'components/grids/CustomGrid';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';

interface paramData {
  condition: any;
  editable: boolean;
  onSelectionChanged: any;
}

export interface FacilityGroupCondition {
  equipmentGroup?: string;
  eqpParaNo?: string;
  eqpParaName?: string;
}

const FacilityGroupModalGrid = forwardRef((props: paramData, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<any[]>([]);
  const [condition, setCondition] = useState<FacilityGroupCondition>({
    equipmentGroup: '',
    eqpParaNo: '',
    eqpParaName: '',
  });

  const layoutDefinition = [
    {
      binding: 'equipmentGroup',
      header: String(t('com.label.설비군', '설비군')),
      width: 100,
      align: 'center',
    },
    {
      binding: 'eqpParaNo',
      header: String(t('ip.label.설비 ParameterNo', '설비 ParameterNo')),
      width: 147,
      align: 'center',
    },
    {
      binding: 'eqpParaName',
      header: String(t('ip.label.설비 Parameter명', '설비 Parameter명')),
      width: '*',
      align: 'left',
    },
  ];

  const listner = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const name = e.target.name;
    setCondition({ ...condition, [name]: e.target.value });
  };

  const handleSearch = () => {
    selectFromIpEqpParaMaster(condition).then((result: any[]) => {
      setRowData(result);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  return (
    <div>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ip.label.설비Parameter', '설비Parameter')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="twoCol">
              <SearchCols>
                <label>{t('com.label.설비군', '설비군')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="equipmentGroup"
                  placeholder={String(
                    t('com.label.설비군을 입력해 주세요.', '설비군을 입력해 주세요.')
                  )}
                  value={condition?.equipmentGroup}
                  onChange={listner}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.설비 ParameterNo', '설비 ParameterNo')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="eqpParaNo"
                  placeholder={String(
                    t(
                      'com.label.설비파라미터번호를 입력해 주세요.',
                      '설비파라미터번호를 입력해 주세요.'
                    )
                  )}
                  value={condition?.eqpParaNo}
                  onChange={listner}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="none">
              <SearchCols>
                <label>{t('ip.label.설비 Parameter명', '설비 Parameter명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="eqpParaName"
                  placeholder={String(
                    t(
                      'com.label.설비파라미터명을 입력해 주세요.',
                      '설비파라미터명을 입력해 주세요.'
                    )
                  )}
                  value={condition?.eqpParaName}
                  onChange={listner}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons setReset={setCondition} onSearch={handleSearch} />
        </SearchBoxRow>
      </SearchBox>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={335}
        allowPinning={false}
        isReadOnly={true}
        autoCheck={true}
        rowSelection={'single'}
        style={{ marginTop: '27px' }}
        initialized={onInitialized}
        onChangeCheckedItem={(items) => props.onSelectionChanged(items)}
      />
    </div>
  );
});

FacilityGroupModalGrid.displayName = 'FacilityGroupModalGrid';
export default memo(FacilityGroupModalGrid);
